'use client'

import { useEffect, useMemo, useRef, useState, useCallback } from 'react'

import { LoaderCircle } from 'lucide-react'
import Countdown, { zeroPad } from 'react-countdown'

import { LevelFooter } from '../Footer'
import { HUD } from '../HUD'
import { LevelToolbar } from '../Toolbar'

import { useLevelContext } from '@/features/level-play'
import { ChallengeStep, useFlow } from '@/shared/lib/steps-flow'
import { showErrorAlert } from '@/shared/lib/ui'
import { cn } from '@/shared/lib/utils'
import { Media } from '@/shared/types/media'
import { Button } from '@/shared/ui/Button'

interface GameMemoryProps {
  step: ChallengeStep
  challengeId: number
}

interface Card {
  main_image: Media
  secondary_image?: Media
}

interface CardImage {
  url: string
  pairId: number
}

const REVEAL_MS_DEFAULT = 6000
const MISMATCH_MS_DEFAULT = 1000

export function GameMemory({ challengeId }: GameMemoryProps) {
  const { state, continueDisabled, goNext, loseLife, enableContinue } =
    useFlow()
  const { challenges, isSubmitting, onChallengeAnswer } = useLevelContext()

  const challenge = challenges.find(c => c.id === challengeId)
  const meta = challenge?.meta
  const showCards = meta?.show_cards ?? true
  const cards =
    challenge?.challenge_options?.length
      ? challenge.challenge_options.map(opt => ({
          main_image: opt.meta?.main_image,
          secondary_image: opt.meta?.secondary_image
        }))
      : meta?.cards ?? []
  const revealMs = REVEAL_MS_DEFAULT
  const mismatchMs = MISMATCH_MS_DEFAULT

  const baseBoard = useMemo<CardImage[]>(() => {
    return cards.flatMap((card, index) => {
      const primary: CardImage = {
        url: card?.main_image?.file?.url,
        pairId: index
      }
      const secondary: CardImage = card?.secondary_image?.file?.url
        ? { url: card.secondary_image.file.url, pairId: index }
        : primary
      return [primary, secondary]
    })
  }, [cards])

  const [board, setBoard] = useState<CardImage[]>([])
  const [flipped, setFlipped] = useState<number[]>([])
  const [matched, setMatched] = useState<Set<number>>(new Set())
  const [locked, setLocked] = useState<boolean>(false)
  const [revealing, setRevealing] = useState<boolean>(showCards)
  const [countdownTarget, setCountdownTarget] = useState<number | null>(null)

  const [mismatchPair, setMismatchPair] = useState<number[] | null>(null)

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const initializeBoard = useCallback(() => {
    const seed =
      typeof challenge?.uid === 'string'
        ? Array.from(challenge.uid).reduce(
            (acc, ch) => acc + ch.charCodeAt(0),
            0
          )
        : undefined

    const shuffled = fisherYates(baseBoard, seed)
    setBoard(shuffled)
    setMatched(new Set())
    setMismatchPair(null)
    setFlipped(showCards ? shuffled.map((_, i) => i) : [])
    setLocked(showCards)
    setRevealing(showCards)

    if (showCards) {
      const target = Date.now() + revealMs
      setCountdownTarget(target)
      const t = setTimeout(() => {
        if (!mountedRef.current) return
        setFlipped([])
        setLocked(false)
        setRevealing(false)
      }, revealMs)
      return () => clearTimeout(t)
    } else {
      setCountdownTarget(null)
      setRevealing(false)
      setLocked(false)
    }
  }, [baseBoard, showCards, revealMs, challenge?.uid])

  useEffect(() => {
    const cleanup = initializeBoard()
    return () => {
      if (typeof cleanup === 'function') cleanup()
    }
  }, [initializeBoard])

  const handleCardClick = useCallback(
    (index: number) => {
      if (locked) return
      if (matched.has(index)) return
      if (flipped.includes(index)) return

      setFlipped(prev => {
        const next = [...prev, index]
        if (next.length < 2) return next

        setLocked(true)
        const [a, b] = next
        const isMatch = board[a].pairId === board[b].pairId

        if (isMatch) {
          setMatched(prevM => {
            const m = new Set(prevM)
            m.add(a)
            m.add(b)
            return m
          })
          setTimeout(() => {
            if (!mountedRef.current) return
            setFlipped([])
            setLocked(false)
          }, 0)
        } else {
          loseLife?.()
          setMismatchPair([a, b])
          const t = setTimeout(() => {
            if (!mountedRef.current) return
            setMismatchPair(null)
            setFlipped([])
            setLocked(false)
          }, mismatchMs)
          void t
        }

        return next
      })
    },
    [board, flipped, locked, matched, loseLife, mismatchMs]
  )

  const handleComplete = async () => {
    try {
      const { ok } = await onChallengeAnswer({
        uid: challenge.uid,
        answer: `${matched.size} cartas encontradas`,
        skipValidation: true
      })
      if (ok) enableContinue()
    } catch (error) {
      showErrorAlert(error)
    }
  }

  const allMatched = matched.size === board.length && board.length > 0

  useEffect(() => {
    if (!allMatched || !challenge || state.phase !== 'READY') return
    handleComplete()
  }, [allMatched, matched.size, challenge])

  const gridClasses = useMemo(() => {
    const n = board.length
    if (n <= 2) return 'grid-cols-2'
    if (n <= 4) return 'grid-cols-2'
    if (n <= 6) return 'grid-cols-3'
    if (n <= 8) return 'grid-cols-4'
    if (n <= 12) return 'grid-cols-6'
    return 'grid-cols-8'
  }, [board.length])

  return (
    <HUD.Card>
      <HUD.Header challenge={challenge} />
      <HUD.Toolbar>
        <LevelToolbar />
      </HUD.Toolbar>

      <HUD.Body className="flex flex-col items-center justify-center min-h-[60vh] w-full gap-4">
        <HUD.Description challenge={challenge} />

        {showCards && countdownTarget && (
          <div className="block text-center font-bold text-lg text-gray-700 w-full h-[25px]">
            <Countdown
              autoStart
              date={countdownTarget}
              renderer={({ total, completed }) =>
                completed ? (
                  ''
                ) : (
                  <span>
                    Começando em{' '}
                    <span className="text-primary font-bold">
                      {zeroPad(Math.floor(total / 1000))}
                    </span>
                    ...
                  </span>
                )
              }
            />
          </div>
        )}

        <div
          className={cn('grid gap-2 w-full max-w-[420px] mx-auto', gridClasses)}
        >
          {board.map((card, i) => {
            const isMatched = matched.has(i)
            const isFlipped = isMatched || flipped.includes(i) || revealing
            const isMismatch =
              !!mismatchPair?.includes(i) && flipped.includes(i) && !isMatched

            return (
              <button
                key={i}
                type="button"
                aria-label={
                  isMatched ? 'Par encontrado' : 'Carta do jogo da memória'
                }
                aria-pressed={isFlipped}
                disabled={locked || isMatched || revealing}
                onClick={() => handleCardClick(i)}
                className={cn(
                  `memory-card relative text-center font-bold text-4xl select-none
                    transition-all duration-200 perspective-1000 rounded-xl
                    bg-white flex items-center justify-center aspect-square
                    focus:outline-none focus:ring-2 focus:ring-primary/60`,
                  {
                    'cursor-default': isMatched,
                    'cursor-pointer': !isMatched && !locked && !revealing,
                    'animate-card-shake': isMismatch
                  },
                  isFlipped && 'is-flipped'
                )}
              >
                <div
                  className={cn(
                    `card-face card-front absolute inset-0 w-full h-full backface-hidden
                      flex items-center justify-center rounded-xl shadow border-3
                      bg-[#eee] z-20 bg-cover bg-center`,
                    {
                      'border-green-400': isMatched,
                      'border-error ring-2 ring-error': isMismatch,
                      'border-jacaranda': !isMatched && !isMismatch
                    }
                  )}
                  style={{ backgroundImage: `url(${card.url})` }}
                />
                <div
                  className={`
                    card-face card-back border-3 border-jacaranda
                    absolute inset-0 w-full h-full backface-hidden rounded-xl
                    bg-center bg-cover z-10 bg-[url('/bgs/back-card.png')]
                  `}
                />
              </button>
            )
          })}
        </div>
      </HUD.Body>

      <LevelFooter>
        <Button
          type="button"
          onClick={goNext}
          className="w-full font-bold btn btn-info"
          disabled={continueDisabled || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <LoaderCircle className="size-5 animate-spin" />
              <span>Carregando</span>
            </>
          ) : (
            <span>Continuar</span>
          )}
        </Button>
      </LevelFooter>
    </HUD.Card>
  )
}

function fisherYates<T>(arr: T[], seed?: number): T[] {
  const a = arr.slice()
  let s = seed ?? Date.now()
  const rand = () => {
    s = (s * 1664525 + 1013904223) % 2 ** 32
    return s / 2 ** 32
  }
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}
